











































































































import gql from 'graphql-tag';
import { mapActions, mapGetters } from 'vuex';
import { BDropdown, BDropdownItem } from 'bootstrap-vue';
import { GridLayout, GridItem } from 'vue-grid-layout';
import { get, pick, without } from 'lodash';
import {
  format,
  addDays,
  subYears,
  subDays,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
} from 'date-fns';
import ZenButton from '@reciprocity/button';
import LoadingSpinner from '@reciprocity/loading-spinner';
import Block from './DashboardBlock.vue';
import Error404Page from '@/pages/error/Error404Page.vue';
import { userRoleCanEdit } from '../../utils/DashboardUtils';

const DATE_FORMAT = 'yyyy-MM-dd';

export const DASHBOARD_QUERY = gql`
  query DashboardById($dashboardId: ID!) {
    dashboard: node(id: $dashboardId) {
      ... on Dashboard {
        id
        title {
          value
        }
        blocks {
          value {
            edges {
              node {
                nodeId: id
                ... on DashboardBlock {
                  id
                  dbId
                  layout {
                    value
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export default {
  name: 'Dashboard',
  components: {
    Block,
    GridItem,
    GridLayout,
    ZenButton,
    LoadingSpinner,
    Error404Page,
    BDropdown,
    BDropdownItem,
  },
  props: {
    dashboardId: {
      type: String,
      required: true,
    },
  },
  apollo: {
    dashboard: {
      query: DASHBOARD_QUERY,
      variables() {
        const { dashboardId } = this;

        return {
          dashboardId,
        };
      },
      update(result) {
        if (!result.dashboard) return;

        const id = result.dashboard.id;
        const title = get(result.dashboard, 'title.value');

        this.setPageTitle({ title, visible: false });

        const layout = get(result.dashboard, 'blocks.value.edges', []).map(edge => {
          return {
            // Backend is returning strings for JSON fields
            // TODO: Remove JSON parse
            ...JSON.parse(get(edge, 'node.layout.value'), {}),
            // TODO: Remove this workaround because backend i is wrong
            i: get(edge, 'node.nodeId'),
            id: get(edge, 'node.id'),
          };
        });

        this.savePreviousLayout(layout);

        return {
          id,
          title,
          layout,
        };
      },
      error(error) {
        // Do not show error if the dashboard is not found
        if (error.graphQLErrors[0].message.includes('None')) return;

        this.showAlert({
          type: 'error',
          message: 'Failed to load Dashboard.',
        });
      },
    },
    userRole: {
      query: gql`
        query GetUserRole {
          currentUser {
            userRole {
              value
            }
          }
        }
      `,
      update(result) {
        return get(result, 'currentUser.userRole.value');
      },
    },
  },
  data() {
    const today = addDays(new Date(), 1);
    const aYearAgo = subYears(today, 1);
    const aMonthAgo = subMonths(today, 1);
    const dateRange = [aYearAgo, today].map(date => format(date, DATE_FORMAT));

    const dateRangeShortcuts = [
      {
        text: 'Last 7 days',
        onClick: () => [subDays(today, 7), today],
      },
      {
        text: 'Last 30 days',
        onClick: () => [subDays(today, 30), today],
      },
      {
        text: 'Last month',
        onClick: () => [startOfMonth(aMonthAgo), endOfMonth(aMonthAgo)],
      },
      {
        text: 'Last 12 months',
        onClick: () => [subYears(today, 1), today],
      },
      {
        text: 'Last year',
        onClick: () => [startOfYear(aYearAgo), endOfYear(aYearAgo)],
      },
    ];

    return {
      dashboard: null,
      userRole: null,
      previousLayout: {},
      dateRange,
      dateRangeShortcuts,
      highlighted: [],
      printing: false,
    };
  },
  computed: {
    ...mapGetters(['featureFlags']),
    canEdit() {
      return userRoleCanEdit(this.userRole);
    },
  },
  methods: {
    ...mapActions({
      showModal: 'modals/show',
      showAlert: 'alerts/show',
      setPageTitle: 'setPageTitle',
    }),
    async onEditDashboard() {
      try {
        const updatedDashboard = await this.showModal({
          type: 'DashboardCard',
          data: {
            cardTitle: 'Edit Dashboard',
            dashboard: this.dashboard,
          },
        });

        // TODO: refactor this to use the whole object
        this.dashboard.title = updatedDashboard.title.value;
      } catch (e) {
        // User closed modal
      }
    },
    async onAddNewBlock() {
      try {
        const block = await this.showModal({
          type: 'DashboardBlockCard',
          data: {
            cardTitle: 'Add Block',
            dashboardId: this.dashboard.id,
          },
        });

        this.dashboard.layout = [
          ...this.dashboard.layout,
          {
            i: block.nodeId,
            id: block.id,
            x: 0,
            y: 0,
            w: 1,
            h: 1,
          },
        ];

        this.highlightItem(block.nodeId);
      } catch (e) {
        // User closed modal
      }
    },
    async onShareDashboard() {
      try {
        await this.showModal({
          type: 'ShareDashboardCard',
          data: {
            dashboardTitle: this.dashboard.title,
            dashboardId: this.dashboard.id,
          },
        });
      } catch (e) {
        // User closed modal
      }
    },
    onLayoutUpdated(layout) {
      layout.forEach(item => {
        const previous = this.previousLayout[item.i];
        const attrs = ['i', 'x', 'y', 'w', 'h'];

        const previousItemLayout = JSON.stringify(pick(previous, attrs));
        const currentItemLayout = JSON.stringify(pick(item, attrs));

        // Check if the item layout has changed
        if (previousItemLayout === currentItemLayout) return;

        this.$apollo.mutate({
          mutation: gql`
            mutation UpdateBlock($id: ID!, $input: UpdateDashboardBlockInput!) {
              updateDashboardBlock(id: $id, input: $input) {
                result {
                  id
                  layout {
                    value
                  }
                }
              }
            }
          `,
          variables: {
            id: item.id,
            input: {
              layout: currentItemLayout,
            },
          },
        });
      });
      this.savePreviousLayout(layout);
    },
    onBlockRemoved(blockId) {
      const blockLayoutIndex = this.dashboard.layout.findIndex(layout => layout.i === blockId);
      this.dashboard.layout.splice(blockLayoutIndex, 1);
    },
    onFilterClear() {
      this.dateRange = null;
    },
    onPrint() {
      this.printing = true;

      setTimeout(() => {
        window.print();
      }, 1000);

      setTimeout(() => {
        this.printing = false;
      }, 2000);
    },
    savePreviousLayout(layout) {
      // Save layout to calculate difference later
      this.previousLayout = layout.reduce((previous, item) => {
        // Copy object
        previous[item.i] = { ...item };
        return previous;
      }, {});
    },
    highlightItem(id) {
      this.highlighted.push(id);
      setTimeout(() => {
        this.highlighted = without(this.highlighted, id);
      }, 2000);
    },
  },
};
